import React, { FC, Suspense, useCallback } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Route, Routes as ReactRoutes, useLocation } from "react-router-dom";

import { useCookieConsent } from "@/shared/contexts";
import { pubSub, TOPIC_ERROR, useAuth } from "@/shared/hooks";

import { BackdropWithSpinner, EnvironmentDisplay } from "./components/atoms";
import { CookieConsent } from "./components/features";
import { RedirectComponent } from "./components/organisms";
import { Routes } from "./Routes";
import { RoutesPublic } from "./RoutesPublic";
import { Scope } from "./shared/hooks/useAuth/types";

const Editor = React.lazy(() => import("./components/features/Theme/Editor/Editor"));

const App: FC = () => {
    const location = useLocation();
    const { user } = useAuth();
    const { i18n } = useTranslation();
    const { isLoadingGTM } = useCookieConsent();

    const LanguageHelmet = useCallback(() => {
        return (
            <Helmet>
                <html lang={i18n.language} />
            </Helmet>
        );
    }, [i18n.language]);

    if (isLoadingGTM) return <BackdropWithSpinner />;

    return (
        <>
            <LanguageHelmet />
            <Suspense fallback={<BackdropWithSpinner />}>
                <ReactRoutes>
                    {user.isAuthenticated && <Route path="*" element={<Routes />} />}
                    {!user.isAuthenticated && (
                        <Route
                            path="*"
                            element={
                                <ErrorBoundary
                                    resetKeys={[location.key]}
                                    FallbackComponent={RedirectComponent}
                                    onError={(error) => pubSub.publish(TOPIC_ERROR, { error })}
                                >
                                    <RoutesPublic />
                                </ErrorBoundary>
                            }
                        />
                    )}
                </ReactRoutes>
            </Suspense>

            {!user.isAuthenticated && <CookieConsent />}
            {user.isScope(Scope.Color) && (
                <Suspense fallback={<BackdropWithSpinner />}>
                    <Editor />
                </Suspense>
            )}
            <EnvironmentDisplay />
        </>
    );
};

export default App;
